import { useMemo, useState } from "react";
import { useFormik } from "formik";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import {
  DynamicOrderObject,
  useUpdateProcessStepDynamicFieldsMutation,
} from "shared/api";
import { handleRequestError } from "shared/helpers";
import { EditingSettingField, ProcessStepSettingsValues } from "shared/types";

interface UseEditProcessStepSettingsProps {
  processStepId?: number;
  dynamicProcessStepFields?: DynamicOrderObject;
  refetchOrderProcessStepTracker?: () => void;
}

export const useEditProcessStepSettings = ({
  processStepId,
  dynamicProcessStepFields,
  refetchOrderProcessStepTracker,
}: UseEditProcessStepSettingsProps) => {
  const { t } = useTranslation();

  const [api, contextHolder] = notification.useNotification();

  const [editingField, setEditingField] = useState<EditingSettingField | null>(
    null
  );

  const [updateProcessStepDynamicFields, { isLoading: isSavingSettings }] =
    useUpdateProcessStepDynamicFieldsMutation();

  const initialValues = useMemo(
    () => ({
      dynamic_process_step_fields: dynamicProcessStepFields ?? {},
    }),
    [dynamicProcessStepFields]
  );

  const settingsFormik = useFormik<ProcessStepSettingsValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!processStepId) return;

      try {
        await updateProcessStepDynamicFields({
          id: processStepId,
          dynamic_process_step_fields: values.dynamic_process_step_fields,
        }).unwrap();

        api.success({
          message: t("process-step.success-title"),
          description: t("configCardColumns.updateSuccess"),
        });

        setEditingField(null);
        refetchOrderProcessStepTracker?.();
      } catch (error) {
        api.error({
          message: t("process-step.error"),
          description: handleRequestError(
            error,
            t("configCardColumns.updateError")
          ),
        });
      }
    },
  });

  const startEditingField = (field: EditingSettingField) => {
    settingsFormik.resetForm();
    setEditingField(field);
  };

  const cancelEditingField = () => {
    settingsFormik.resetForm();
    setEditingField(null);
  };

  return {
    settingsFormik,
    editingField,
    startEditingField,
    cancelEditingField,
    isSavingSettings,
    contextHolder,
  };
};
